import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import { useAuth } from '../context/AuthContext';

const extractError = (err, fallback) =>
  err.response?.data?.message ||
  err.response?.data?.errors?.[0]?.message ||
  fallback;

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOtp, resendOtp } = useAuth();

  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(5);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const data = await login(email, password);
      setOtpToken(data.otpToken);
      setOtpEmail(data.email || email);
      setOtpExpiresIn(data.expiresInMinutes || 5);
      setCode('');
      setStep('otp');
    } catch (err) {
      setError(extractError(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await verifyOtp(otpToken, code);
      navigate(from, { replace: true });
    } catch (err) {
      setError(extractError(err, 'Could not verify code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setResending(true);

    try {
      const data = await resendOtp(otpToken);
      setOtpToken(data.otpToken);
      setOtpExpiresIn(data.expiresInMinutes || 5);
      setCode('');
      setInfo('A new code has been sent.');
    } catch (err) {
      setError(extractError(err, 'Could not resend code. Please sign in again.'));
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setStep('credentials');
    setOtpToken('');
    setCode('');
    setError('');
    setInfo('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.default} 50%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
      }}
    >
      <Fade in timeout={800}>
        <Card
          sx={{
            maxWidth: 440,
            width: '100%',
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 2,
                  background: 'linear-gradient(135deg, #6C63FF 0%, #9D97FF 100%)',
                  boxShadow: '0 4px 20px rgba(108, 99, 255, 0.3)',
                }}
              >
                {step === 'otp' ? <MailOutline sx={{ fontSize: 28 }} /> : <DiamondOutlined sx={{ fontSize: 28 }} />}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {step === 'otp' ? 'Check your email' : 'Prince Seating'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step === 'otp'
                  ? `We sent a 6-digit code to ${otpEmail}`
                  : 'Sign in to your dashboard'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {info}
              </Alert>
            )}

            {step === 'credentials' ? (
              <Box component="form" onSubmit={handleCredentialsSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{ mb: 2.5 }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  sx={{ mb: 3 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                </Button>

                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    underline="hover"
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleOtpSubmit}>
                <TextField
                  fullWidth
                  label="Verification code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  sx={{
                    mb: 2,
                    '& input': {
                      letterSpacing: '0.4em',
                      fontSize: '1.25rem',
                      textAlign: 'center',
                      fontWeight: 600,
                    },
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, textAlign: 'center' }}>
                  This code expires in {otpExpiresIn} minutes.
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || code.length !== 6}
                  sx={{ mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & sign in'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleBack}
                    disabled={loading}
                    underline="hover"
                  >
                    Use a different account
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResend}
                    disabled={resending || loading}
                    underline="hover"
                  >
                    {resending ? 'Sending…' : 'Resend code'}
                  </Link>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default Login;
